import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Upload, Trash2, Store, IndianRupee } from "lucide-react";
import api from "@/lib/api";
import { fileToDataUrl, invalidateSite, DEFAULT_LOGO } from "@/lib/site";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const errDetail = (e, f) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : f; };

const ImageField = ({ label, value, onChange, hint, testId, preview = "h-20 w-20" }) => {
  const ref = useRef();
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { onChange(await fileToDataUrl(f)); } catch (err) { toast.error(err.message); }
    e.target.value = "";
  };
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-4 mt-2">
        {value ? <img src={value} alt={label} className={`${preview} rounded-xl object-contain bg-white border border-slate-200`} data-testid={`${testId}-preview`} />
          : <div className={`${preview} rounded-xl bg-slate-100 border border-dashed border-slate-300`} />}
        <div>
          <input ref={ref} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`${testId}-input`} />
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => ref.current?.click()} className="rounded-lg" data-testid={`${testId}-btn`}><Upload className="w-4 h-4 mr-1" /> Upload</Button>
            {value && <Button type="button" variant="ghost" size="sm" onClick={() => onChange("")} data-testid={`${testId}-remove`}><Trash2 className="w-4 h-4" /></Button>}
          </div>
          <p className="font-body text-xs text-slate-400 mt-1">{hint}</p>
        </div>
      </div>
    </div>
  );
};

export const SiteSettingsCard = () => {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/site").then((r) => setForm(r.data)).catch(() => {}); }, []);
  if (!form) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      const { payments_ready, ...body } = form;
      const r = await api.put("/admin/site", { ...body, assessment_price: Number(form.assessment_price) || 0 });
      setForm(r.data); invalidateSite(); toast.success("Website settings saved");
    } catch (e) { toast.error(errDetail(e, "Could not save")); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6" data-testid="site-settings-card">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center"><Store className="w-6 h-6 text-primary" /></div>
        <div><h2 className="font-heading font-bold text-lg text-slate-900">Website & payments</h2><p className="font-body text-sm text-slate-500">Logo, tagline and the UPI details customers pay to.</p></div>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <ImageField label="Site logo" value={form.logo} onChange={(v) => setForm((f) => ({ ...f, logo: v }))} hint={`Square, under 500KB. Default: bundled Motley Mentors logo.`} testId="site-logo" />
        <div><Label>Tagline</Label><Input value={form.tagline} onChange={set("tagline")} className="mt-2 h-11 rounded-xl" data-testid="site-tagline" /></div>
        <div><Label>Contact email <span className="text-slate-400 font-normal text-xs">(shown when payments are off)</span></Label><Input value={form.contact_email} onChange={set("contact_email")} className="mt-2 h-11 rounded-xl" data-testid="site-contact-email" /></div>
        <div><Label>WhatsApp number</Label><Input value={form.whatsapp} onChange={set("whatsapp")} placeholder="+91…" className="mt-2 h-11 rounded-xl" data-testid="site-whatsapp" /></div>
      </div>
      <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5 space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="font-heading font-bold text-slate-900 flex items-center gap-2"><IndianRupee className="w-4 h-4 text-primary" /> UPI payments</p>
          <span className={`font-body text-xs px-2.5 py-1 rounded-full ${form.upi_id ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`} data-testid="payments-status-badge">{form.upi_id ? "Accepting payments" : "Not accepting yet — add a UPI ID"}</span>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div><Label>UPI ID</Label><Input value={form.upi_id} onChange={set("upi_id")} placeholder="motleymentors@upi" className="mt-2 h-11 rounded-xl bg-white font-mono" data-testid="site-upi-id" /></div>
          <div><Label>Payee name</Label><Input value={form.upi_name} onChange={set("upi_name")} className="mt-2 h-11 rounded-xl bg-white" data-testid="site-upi-name" /></div>
          <div><Label>Assessment price ({form.currency})</Label><Input type="number" min="0" value={form.assessment_price} onChange={set("assessment_price")} className="mt-2 h-11 rounded-xl bg-white" data-testid="site-assessment-price" /></div>
        </div>
        <ImageField label="UPI QR code" value={form.upi_qr} onChange={(v) => setForm((f) => ({ ...f, upi_qr: v }))} hint="Export from your UPI app. Shown on every checkout." testId="site-upi-qr" preview="h-28 w-28" />
      </div>
      <Button onClick={save} disabled={busy} className="rounded-xl font-heading font-bold" data-testid="site-save-btn">{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save website settings</Button>
    </div>
  );
};
